import { IAuthenticationService } from "./IAuthenticationService";
import { AuthenticationDetails, CognitoRefreshToken, CognitoUser, CognitoUserAttribute, CognitoUserPool, CognitoUserSession } from 'amazon-cognito-identity-js';
import { ISignupRequest } from "./ISignupRequest";

export default class AWSAuthenticationService implements IAuthenticationService {
    private userPool: CognitoUserPool;
    private config: Record<string, string>;

    constructor(config: Record<string, string>) {
        this.config = config;

        const poolData = {
            UserPoolId: this.config.userPoolId,
            ClientId:   this.config.appClientId
        };

        this.userPool = new CognitoUserPool(poolData);
    }

    login(email: string, password: string): Promise<string> {
        const authenticationDetails = new AuthenticationDetails(
            {
                Username: email,
                Password: password
            }
        );
        
        const userData = { Username: email, Pool: this.userPool };
        const cognitoUser = new CognitoUser(userData);
        
        return new Promise((resolve, reject) => {
            cognitoUser.authenticateUser(authenticationDetails, {
                onSuccess: (result) => {
                    resolve(result.getIdToken().getJwtToken());
                },
                
                onFailure:(err) => {
                    console.log("[AWSAuthenticationService.login()] Error during login", err);
                    reject(new Error(err));
                },

                // newPasswordRequired: (userAttributes, requiredAttributes) => {
                //     console.log("userAttributes", userAttributes)
                //     console.log("requiredAttributes", requiredAttributes)
                //     // User was signed up by an admin and must provide new
                //     // password and required attributes, if any, to complete
                //     // authentication.
        
                //     // the api doesn't accept this field back
                //     delete userAttributes.email_verified;
                    
                //     cognitoUser.completeNewPasswordChallenge("Foo1234!", requiredAttributes, {
                //         onSuccess(session) {
                //             console.log("session", session)
                //         },
                //         onFailure(err) {
                //             console.log("[AWSAuthenticationService.completeNewPasswordChallenge()] Error during completeNewPasswordChallenge", err);
                //             reject(err);
                //         },
                //     });
                // }
            });
        });
    }

    logout(): Promise<void> {
        this.userPool.getCurrentUser()?.signOut();

        return Promise.resolve();
    }

    signUp(request: ISignupRequest): Promise<void> {
        const attributes = [
            new CognitoUserAttribute({ Name: "email",       Value: request.email }),
            new CognitoUserAttribute({ Name: "name",        Value: request.name }),
            new CognitoUserAttribute({ Name: "family_name", Value: request.surname })
        ];

        return new Promise((resolve, reject) => {
            this.userPool.signUp(request.email, request.password, attributes, [], (err, result) => {
                if (err) 
                {
                    console.log("[AWSAuthenticationService.signUp()] - Error during sign up process", err);
                    reject(err);
                }
                
                console.log("[AWSAuthenticationService.signUp()] - Sign up sucess! Waiting for code...");
                resolve();
            });
        });
    }

    confirmRegistrationByCode(request: { email: string, code: string }): Promise<void> {
        const userData = { 
            Username:   request.email,
            Pool:       this.userPool
        };
        
        const cognitoUser = new CognitoUser(userData);

        return new Promise((resolve, reject) => {
            cognitoUser.confirmRegistration(request.code, true, (err, result) => {
                if (err) {
                    console.log("[AWSAuthenticationService.confirmRegistrationByCode()] - Error during confirm registration by code", err);
                    reject(err);
                }

                console.log("[AWSAuthenticationService.confirmRegistrationByCode()] - Registration confirmed", result);
                resolve();
            });
        });
    }

    resendConfirmationCode(email: string): Promise<void> {
        const userData = { 
            Username:   email,
            Pool:       this.userPool
        };
        
        const cognitoUser = new CognitoUser(userData);

        return new Promise((resolve, reject) => {
            cognitoUser.resendConfirmationCode((err, result) => {
                if (err) {
                    console.log("[AWSAuthenticationService.resendConfirmationCode()] - Error during resending registration code", err);
                    reject(err);
                }

                console.log("[AWSAuthenticationService.resendConfirmationCode()] - Code sent!");
                resolve();
            });
        });
    }

    forgotPassword(email: string): Promise<void> {
        const userData = { 
            Username:   email,
            Pool:       this.userPool
        };
        
        const cognitoUser = new CognitoUser(userData);

        return new Promise((resolve, reject) => {
            cognitoUser.forgotPassword({
                onSuccess(data) {
                    console.log("[AWSAuthenticationService.forgotPassword()] - Success", data);
                    resolve();
                },
                onFailure(err) {
                    console.log("[AWSAuthenticationService.forgotPassword()] - Error during forgot password procedure", err);
                    reject(err);
                },
            });
        });
    }

    confirmNewPassword(email: string, verificationCode: string, newPassword: string): Promise<void> {
        const userData = { 
            Username:   email,
            Pool:       this.userPool
        };
        
        const cognitoUser = new CognitoUser(userData);

        return new Promise((resolve, reject) => {
            cognitoUser.confirmPassword(verificationCode, newPassword, {
                onSuccess() {
                    console.log("[AWSAuthenticationService.confirmNewPassword()] - Success");
                    resolve();
                },
                onFailure(err) {
                    console.log("[AWSAuthenticationService.confirmNewPassword()] - Error during forgot password procedure", err);
                    reject(err);
                },
            });
        });
    }

    refreshSession(): Promise<string> {
        const cognitoUser = this.userPool.getCurrentUser();
        if(!cognitoUser) return Promise.reject("[AWSAuthenticationService.refreshSession()] - cognitoUser is Null");
        
        let refreshToken: string;
        cognitoUser.getSession((err: any, result: CognitoUserSession) => {
            refreshToken = result.getRefreshToken().getToken();
        });

        return new Promise((resolve, reject) => {
            cognitoUser.refreshSession(new CognitoRefreshToken({ RefreshToken: refreshToken }), (err, session) => {
                if (err) 
                {
                    console.log("[AWSAuthenticationService.refreshSession()] - Error during refresh session", err);
                    reject(new Error(err));
                } 
                else 
                {
                    console.log("[AWSAuthenticationService.refreshSession()] - token refreshed");
                    resolve(session.getIdToken().getJwtToken());
                }
            });
        });
    }
}